"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
export default function HeroBookingForm() {
  return <section data-section-id="4582" id="herobookingform" className="relative min-h-screen w-full bg-background text-foreground overflow-hidden">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      {}
      <div className="hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-muted-foreground whitespace-nowrap" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          Est. — Marga Travel
        </div>
      </div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-20 pt-28 sm:pt-32 lg:pt-40 pb-20 lg:pb-24 min-h-screen flex flex-col">
        <div className="text-center mb-12 lg:mb-16">
          <h1 className="font-default font-bold text-foreground leading-[0.95] uppercase" style={{
          fontSize: "clamp(2.5rem, 8vw, 5rem)",
          letterSpacing: "-0.02em"
        }}>
            Request a Quote
          </h1>
          <p className="mt-6 text-muted-foreground max-w-2xl mx-auto text-lg">
            Tell us about your dream journey and our travel experts will craft a personalized itinerary just for you.
          </p>
        </div>

        <FormProvider formId="booking_inquiry_form" sectionName="hero-booking-section" className="max-w-4xl mx-auto w-full bg-card p-8 sm:p-12 rounded-lg border border-border shadow-lg" defaultValues={{
        name: "",
        email: "",
        phone: "",
        destination: "",
        travel_dates: "",
        special_request: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true
        },
        destination: {
          required: true
        },
        travel_dates: {
          required: true
        }
      }}>
          <BookingFormContent />
        </FormProvider>
      </div>

      <style>{`
        @keyframes veilScrollPulse {
          0% { transform: translateY(-100%); }
          60% { transform: translateY(100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>
    </section>;
}
function BookingFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="size-16 bg-[#72BF44]/10 rounded-lg flex items-center justify-center mb-6">
          <CheckCircle2 className="size-10 text-[#72BF44]" />
        </div>
        <h3 className="text-2xl font-bold mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage || "Thank you for your request. One of our travel specialists will contact you within 24 hours."}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary/5">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="rounded-lg h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="rounded-lg h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest">Phone Number</FormLabel>
              <FormControl>
                <Input placeholder="+1 (555) 000-0000" className="rounded-lg h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="destination" rules={{
        required: "Please specify a destination"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-bold uppercase tracking-widest">Destination</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Maldives, Paris, Tokyo" className="rounded-lg h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="travel_dates" rules={{
        required: "Travel dates are required"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-xs font-bold uppercase tracking-widest">Preferred Travel Dates</FormLabel>
              <FormControl>
                <Input type="date" className="rounded-lg h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="special_request" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="text-xs font-bold uppercase tracking-widest">Special Requests (Optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us more about your preferences, budget, or specific requirements..." className="rounded-lg min-h-[120px] resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <div className="flex justify-center pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto px-12 py-6 h-auto text-lg font-bold uppercase tracking-widest rounded-lg shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]">
          {isSubmitting ? <>
              <Loader2 className="mr-2 size-5 animate-spin" />
              Processing...
            </> : "Get My Quote"}
        </Button>
      </div>
    </Form>;
}